/**
 * ACTIVE.1 — the single versioned Weapon × Primary configuration authority
 * (audit §I). Family affinity matrices, slopes, and the six Lv.20 raw
 * reference source definitions from `weapon-primary-basic-attack-contract.md`
 * §5/§9. No scattered magic numbers elsewhere — `src/sim/source-profile.ts`'s
 * pure functions consume exactly this constant.
 */

import type { PrimaryStatKey } from './primary-stats.js';
import { PRIMARY_STAT_KEYS } from './primary-stats.js';
import { isFiniteNumber } from '../math.js';
import { WEAPON_FAMILIES, type WeaponFamily } from '../types.js';

export { WEAPON_FAMILIES, type WeaponFamily } from '../types.js';

/** One family's affinity row: weights across a subset of Primary stats, summing to 1.0. */
export type AffinityRow = Partial<Record<PrimaryStatKey, number>>;

export interface WeaponPrimaryProfile {
  version: string;
  damageAffinity: Readonly<Record<WeaponFamily, Readonly<AffinityRow>>>;
  handlingAffinity: Readonly<Record<WeaponFamily, Readonly<AffinityRow>>>;
  /** The stat of the equipped weapon's family that, with the damage class's own stat, raises critical damage. */
  critDamageMainStat: Readonly<Record<WeaponFamily, PrimaryStatKey>>;
  /** +3% per weighted Primary point: the one damage growth rate, used by pricing, monster calibration and a live character alike. */
  damageSlope: number;
  /** contract §4: +0.25% per weighted Primary point. */
  attackRateSlope: number;
  /** contract §9 — Lv.20 raw base values per family (id/family/baseDamage/baseAttackRate). */
  referenceSources: Readonly<Record<WeaponFamily, { readonly id: string; readonly baseDamage: number; readonly baseAttackRate: number }>>;
}

export const WEAPON_PRIMARY_PROFILE_V0: WeaponPrimaryProfile = Object.freeze({
  version: 'weapon-primary-v0-face-value-2026-10-12',
  damageAffinity: Object.freeze({
    HEAVY: Object.freeze({ STR: 0.5, VIT: 0.3, TEC: 0.2 }),
    BALANCED: Object.freeze({ STR: 0.45, TEC: 0.35, AGI: 0.2 }),
    LIGHT: Object.freeze({ AGI: 0.4, TEC: 0.4, STR: 0.2 }),
    POLEARM: Object.freeze({ STR: 0.4, TEC: 0.4, AGI: 0.2 }),
    RANGED: Object.freeze({ TEC: 0.5, STR: 0.3, AGI: 0.2 }),
    CATALYST: Object.freeze({ MAG: 0.55, TEC: 0.25, WIL: 0.2 }),
  }),
  // 2026-09-13-player-power-curve-adjustment.md §2.7: HEAVY/BALANCED/POLEARM/
  // RANGED re-weighted to close the STR:TEC handling-total gap (was exactly
  // 2x TEC over STR); LIGHT/CATALYST unchanged — no narrative case for
  // strength handling a dagger or a catalyst.
  handlingAffinity: Object.freeze({
    HEAVY: Object.freeze({ STR: 0.5, VIT: 0.35, TEC: 0.15 }),
    BALANCED: Object.freeze({ AGI: 0.45, STR: 0.3, TEC: 0.25 }),
    LIGHT: Object.freeze({ AGI: 0.55, TEC: 0.35, STR: 0.1 }),
    POLEARM: Object.freeze({ AGI: 0.35, STR: 0.35, TEC: 0.3 }),
    RANGED: Object.freeze({ AGI: 0.45, TEC: 0.4, STR: 0.15 }),
    CATALYST: Object.freeze({ TEC: 0.45, WIL: 0.35, MAG: 0.2 }),
  }),
  // RANGED is STR on purpose: its first stat is TEC, which already gives the chance.
  critDamageMainStat: Object.freeze({
    HEAVY: 'STR', BALANCED: 'STR', POLEARM: 'STR', LIGHT: 'AGI', RANGED: 'STR', CATALYST: 'MAG',
  }),
  damageSlope: 0.03,
  attackRateSlope: 0.0025,
  referenceSources: Object.freeze({
    HEAVY: Object.freeze({ id: 'reference.heavy', baseDamage: 75.9, baseAttackRate: 0.511 }),
    BALANCED: Object.freeze({ id: 'reference.balanced', baseDamage: 62.8, baseAttackRate: 0.673 }),
    LIGHT: Object.freeze({ id: 'reference.light', baseDamage: 26.2, baseAttackRate: 1.674 }),
    POLEARM: Object.freeze({ id: 'reference.polearm', baseDamage: 50.5, baseAttackRate: 0.837 }),
    // Bow-only cadence/damage adjustment: rate 1.553 * 0.80 and damage 28.6 * 1.35.
    // Projectile geometry, velocity and Primary scaling are unchanged.
    RANGED: Object.freeze({ id: 'reference.ranged_bow', baseDamage: 38.61, baseAttackRate: 1.2424 }),
    // Catalyst's raw source remains unchanged by the Bow-only adjustment.
    // The previous canonical Catalyst/Bow DPS ratio was 1.15; after the
    // Bow-only +8% DPS adjustment it is 1.15 / 1.08. A separate decision is
    // required before modifying Catalyst's own damage or rate.
    CATALYST: Object.freeze({ id: 'reference.catalyst', baseDamage: 41.01080807725525, baseAttackRate: 1.262 }),
  }),
});

export interface WeaponPrimaryProfileValidationResult {
  ok: boolean;
  errors: string[];
}

/** Config validation: all finite, all
 * non-negative, each affinity row sums to 1 within one documented tolerance,
 * version string present. */
export function validateWeaponPrimaryProfile(
  profile: WeaponPrimaryProfile,
  rowSumTolerance = 1e-9,
): WeaponPrimaryProfileValidationResult {
  const errors: string[] = [];
  if (!profile.version || profile.version.length === 0) errors.push('version must be a non-empty string');
  if (!isFiniteNumber(profile.damageSlope) || profile.damageSlope < 0) errors.push(`damageSlope must be finite >= 0 (got ${profile.damageSlope})`);
  if (!isFiniteNumber(profile.attackRateSlope) || profile.attackRateSlope < 0) errors.push(`attackRateSlope must be finite >= 0 (got ${profile.attackRateSlope})`);

  const checkRow = (label: string, family: WeaponFamily, row: Readonly<AffinityRow>) => {
    let sum = 0;
    for (const key of PRIMARY_STAT_KEYS) {
      const w = row[key];
      if (w === undefined) continue;
      if (!isFiniteNumber(w) || w < 0) errors.push(`${label}[${family}].${key} must be finite >= 0 (got ${w})`);
      else sum += w;
    }
    if (Math.abs(sum - 1) > rowSumTolerance) errors.push(`${label}[${family}] weights sum to ${sum}, expected 1 (tolerance ${rowSumTolerance})`);
  };
  for (const family of WEAPON_FAMILIES) {
    checkRow('damageAffinity', family, profile.damageAffinity[family]);
    checkRow('handlingAffinity', family, profile.handlingAffinity[family]);
    const mainStat = profile.critDamageMainStat[family];
    if (!PRIMARY_STAT_KEYS.includes(mainStat)) errors.push(`critDamageMainStat[${family}] must be one of ${PRIMARY_STAT_KEYS.join(', ')} (got ${String(mainStat)})`);
    const ref = profile.referenceSources[family];
    if (!isFiniteNumber(ref.baseDamage) || ref.baseDamage <= 0) errors.push(`referenceSources[${family}].baseDamage must be finite > 0 (got ${ref.baseDamage})`);
    if (!isFiniteNumber(ref.baseAttackRate) || ref.baseAttackRate <= 0) errors.push(`referenceSources[${family}].baseAttackRate must be finite > 0 (got ${ref.baseAttackRate})`);
  }
  return { ok: errors.length === 0, errors };
}
